import { IClientOptions } from 'async-mqtt';
import fs from 'fs';
import { isEmpty, merge as lodashMerge, omit, set as lodashSet } from 'lodash';
import os from 'os';
import path from 'path';

import packageJson from '../../package.json';
import { loadConfigFile, loadJSONFile, loadYAMLFile, saveJSONFile } from './utils';

export const singletonEnforcer = Symbol('singletonEnforcer');

export interface ICommunicationConfig {
  host: string;
  port: number;
  options?: IClientOptions;
}

export type ITransport = 'console' | 'file' | 'broker';
export interface ILoggerConfig {
  prefix?: string;
  level: string;
  relativePath?: string;
  logMaxFilesize: number; // size in bytes
  logMaxFiles: number;
  logFilename: string;
  logsPath?: string;
  transports?: ITransport[];
  topic?: string;
}

export interface IBaseConfig {
  logger?: ILoggerConfig;
  configs?: {
    relativePath: string;
  };
  communication?: ICommunicationConfig;
}

export interface IAppConfig {
  name: string;
  user: string;

  platform: string;
  arch: string;
  env: string;

  applicationPath: string;
  migrationsPath: string;
  tmpPath: string;
  dataPath: string;

  configPath: string;
  configFileName: string;
  configFilePath: string;
  exampleConfigFilePath: string;

  licensePath: string;
  licenseFileName: string;
  licenseFilePath: string;

  brokerFileName: string;
  brokerFilePath: string;
  exampleBrokerFilePath: string;

  aliveTimeout?: number;
  serial?: string;
  sessionId?: string;
}

export interface IModuleDockerImages {
  images: string[];
  imageFile: string;
}

export interface IModuleService {
  type?: 'docker';
  options: Record<string, unknown>;
}

export interface IModuleScriptHook {
  type: 'script';
  command: string;
}

export interface IModuleHooks {
  postinstall?: IModuleScriptHook;
  preuninstall?: IModuleScriptHook;
}


export interface IModuleEndpointEmbed {
  type: 'embed';
  url?: string;
}

export interface IModuleEndpointProxy {
  type: 'proxy';
  port?: number;
  host?: string;
  url?: string;
}

export interface IModuleEndpointStatic {
  type: 'static';
  path: string;
  url?: string;
}

export interface IModuleOptions {
  endpoints?: {
    frontend?: IModuleEndpointEmbed | IModuleEndpointProxy | IModuleEndpointStatic;
    api?: IModuleEndpointEmbed | IModuleEndpointProxy;
  };
  icon?: string;
  color?: string;

  [name: string]: unknown;
}

export const ModuleTypes = [
  'module',
  'interpreter',
  'service',
  'internal',
  'detached',
  'image',
] as const;

export type IModuleLanguage = 'node' | 'ruby' | 'python' | 'other';
export type IModuleType = typeof ModuleTypes[number];
export interface IFbxfile {
  type: IModuleType;
  language: IModuleLanguage;
  platform?: NodeJS.Platform;
  arch?: string;
  image?: string | string[];
  name: string;
  version: string;
  title: string;
  description: string;
  command?: string;
  args: string;
  env?: Record<string, string>;
  bin?: string;
  lib?: string;
  options: IModuleOptions;
  hooks?: IModuleHooks;
  dockerImages?: IModuleDockerImages;
  customEnv?: Record<string, string>;
  service?: IModuleService;
  requiredLicenses?: string[];
}

export interface IRuntimeConfigs {
  aliveTimeout: number;
  serial?: string;
  sessionId?: string;
}

export interface IConfig {
  version: string;
  fbxfile: IFbxfile;
  app: IAppConfig;
  communication: ICommunicationConfig;
  logger: ILoggerConfig;
  globals: object;
  runTime: IRuntimeConfigs;
  $iterator(): Iterator<any>;
}

export interface IBrokerFile {
  username: string;
  password: string;
}

export default class Configs<C extends IConfig = IConfig> {
  protected static singleton: Configs;

  public static set(key: string, value: any, save = true): void {
    return Configs.instance.set(key, value, save);
  }

  public static merge(configs: any, save = true): void {
    Configs.instance.merge(configs, save);
  }

  public static mergeFromFile(filePath: string): void {
    Configs.instance.mergeFromFile(filePath);
  }

  public static save(): void {
    Configs.instance.save();
  }

  public static reload(): IConfig {
    return Configs.instance.load();
  }

  public static destroy(): void {
    this.singleton = undefined;
  }

  protected configs: C;

  private static ALIVE_TIMEOUT = 1000;

  private static BASE_CONFIG: IBaseConfig = {
    logger: {
      level: (process.env.NODE_ENV === 'development') ? 'debug' : 'error',
      relativePath: 'logs',
      logMaxFilesize: 20971520,
      logMaxFiles: 7,
      logFilename: 'application.log',
      topic: 'logs',
      transports: ['console'],
    },
    configs: {
      relativePath: 'config',
    },
    communication: {
      host: 'localhost',
      port: 1883,
    },
  };

  constructor(enforcer: symbol) {
    if (enforcer !== singletonEnforcer) { throw new Error('Cannot construct singleton'); }
    this.load();
  }

  public reload(): C {
    return this.load();
  }

  public load(): C {
    const app: IAppConfig = ({} as undefined as IAppConfig);
    // static configs
    const { logger, configs, communication } = Configs.BASE_CONFIG;

    app.user = 'fancy';
    app.platform = process.platform;
    app.arch = process.arch;
    app.env = process.env.NODE_ENV || 'production';
    app.serial = process.env.SERIAL;

    app.applicationPath = process.cwd();
    app.migrationsPath = path.join(app.applicationPath, 'migrations');
    app.tmpPath = os.tmpdir();
    app.dataPath = process.env.DATA_PATH || app.applicationPath;

    // read fbxfile
    const fbxfileFileName = 'Fbxfile';
    const fbxfileFilePath = path.join(app.applicationPath, fbxfileFileName);
    const fbxfile: IFbxfile = loadYAMLFile(fbxfileFilePath) as IFbxfile;

    // Set application configurations
    const { name } = packageJson;
    app.name = fbxfile.name || name;

    // configs
    const { relativePath } = configs;
    app.configPath = path.join(app.dataPath, relativePath);
    app.configFileName = `configs.${app.platform}-${app.arch}.json`;
    app.configFilePath = path.join(app.configPath, app.configFileName);
    app.exampleConfigFilePath = path.join(app.applicationPath, relativePath, `${app.configFileName}.example`);
    if (!fs.existsSync(app.configPath)) { fs.mkdirSync(app.configPath, { recursive: true }); }

    // license
    app.licensePath = app.applicationPath;
    app.licenseFileName = 'license.lic';
    app.licenseFilePath = path.join(app.licensePath, app.licenseFileName);

    // Broker file
    app.brokerFileName = 'broker.json';
    app.brokerFilePath = path.join(app.dataPath, app.brokerFileName);
    app.exampleBrokerFilePath = `${app.brokerFilePath}.example`;

    // Generate config file when missing
    if (!fs.existsSync(app.configFilePath)) {
      if (fs.existsSync(app.exampleConfigFilePath)) {
        fs.copyFileSync(app.exampleConfigFilePath, app.configFilePath);
      }
    }

    // if development create default broker.json file
    if (app.env === 'development') {
      if (!fs.existsSync(app.brokerFilePath)) {
        if (fs.existsSync(app.exampleBrokerFilePath)) {
          fs.copyFileSync(app.exampleBrokerFilePath, app.brokerFilePath);
        }
      }
    }

    // Load configurations from file
    this.configs = loadJSONFile(app.configFilePath)[app.env] || {};
    this.configs.app = app;
    this.configs.fbxfile = fbxfile;
    this.configs.logger = lodashMerge(logger, this.configs.logger);
    this.configs.communication = lodashMerge(communication, this.configs.communication);
    this.configs.runTime = { aliveTimeout: Configs.ALIVE_TIMEOUT };

    return this.configs;
  }

  public set(key: string, value: any, save = true): void {
    lodashSet(this.configs, key, value);
    if (save) {
      this.save();
    }
  }

  // TODO validate configs before merging?
  public merge(configs: any, save = true): void {
    if (!isEmpty(configs)) {
      lodashMerge(this.configs, configs);
      if (save) {
        this.save();
      }
    }
  }

  public save(): void {
    const { app } = this.configs;
    const { configFilePath, env } = app;
    const configFileContent = loadJSONFile(configFilePath);
    const excluedKeys = ['app', 'fbxfile', 'runTime'];
    configFileContent[env] = omit(this.configs, excluedKeys);
    saveJSONFile(configFilePath, configFileContent);
  }

  public mergeFromFile(filePath: string): void {
    if (filePath && fs.existsSync(filePath)) {
      const configs = loadConfigFile(filePath);
      this.merge(configs);
    }
  }

  static get instance() {
    if (!Configs.singleton) {
      Configs.singleton = new Configs(singletonEnforcer);
    }
    return Configs.singleton;
  }

  static set instance(instance: Configs) {
    Configs.singleton = instance;
  }

  static get configs() {
    return Configs.instance.configs;
  }
}
